import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Checkbox, FormControlLabel, Stack, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { FLUSH } from "redux-persist";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getMerchantConfig, getRmsConfig } from "features/config/configSlice";
import { getSelectedOrder } from "features/orders/orderSlice";
import { addReturnedProducts, getReturnedProducts } from "features/returnedProducts/returnedProductSlice";
import { getAddress } from "features/address/addresSlice";
import { addUploadMediaIds, getUploadMediaIds } from "features/product/productSlice";
import { getEmail, getPhone } from "features/user/userSlice";
import { createAgentDraft } from "utils/api";
import { getApiErrorMessage } from "utils/apiError";
import { isValidEmail, isValidPhoneNumber } from "utils/contactValidation";
import { getReturnCartContext, mergeReturnCartContext } from "utils/returnCartSession";
import { getEncipherencode } from "utils/encipherencode";
import { versionedPath } from "utils/routes";
import { buildAgentDraftPayload, clearAgentDraftAttempt, isAgentDraftItem, prepareAgentDraftAttempt, readAgentDraftAttempt, readAgentDraftReceipt, saveAgentDraftReceipt, clearAgentDraftReceipt, hasPersistedAgentCart, readMixedAgentDraftReceipt, validateAgentDraftResponse } from "utils/agentDraft";
import { clearReasonDraft } from "components/v2/Products/reason";
import PageHeader from "components/v2/Common/PageHeader";
import PageIntro from "components/v2/Common/PageIntro";
import LineItemCard from "components/v2/Common/LineItemCard";

const fields = [
  ["customer_first_name", "First name"], ["customer_last_name", "Last name"],
  ["customer_email", "Email"], ["customer_phone_number", "Phone number"],
  ["customer_address_line_1", "Address line 1"], ["customer_address_line_2", "Address line 2"],
  ["customer_address_city", "City"], ["customer_address_pin", "Postal code"],
  ["customer_address_state", "State"], ["customer_address_country", "Country"],
];

export default function AgentDraftGate({ children, renderMixedCheckout }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const merchant = useSelector(getMerchantConfig);
  const rmsConfig = useSelector(getRmsConfig) || {};
  const selectedOrder = useSelector(getSelectedOrder);
  const products = useSelector(getReturnedProducts);
  const address = useSelector(getAddress);
  const uploads = useSelector(getUploadMediaIds);
  const email = useSelector(getEmail);
  const phone = useSelector(getPhone);
  const savedContext = getReturnCartContext();
  const order = selectedOrder?.order_id ? selectedOrder : savedContext?.selectedOrder || {};
  const contextMatchesOrder = !selectedOrder?.order_id ||
    String(savedContext?.selectedOrder?.order_id) === String(selectedOrder.order_id);
  const cart = products.length ? products : contextMatchesOrder ? savedContext?.returnedProducts || [] : [];
  const agentItems = useMemo(() => cart.filter(isAgentDraftItem), [cart]);
  const normalItemsRef = useRef([]);
  const remainingItems = useMemo(() => {
    const nextItems = cart.filter((item) => !isAgentDraftItem(item));
    if (nextItems.length === normalItemsRef.current.length && nextItems.every((item, index) => item === normalItemsRef.current[index])) {
      return normalItemsRef.current;
    }
    normalItemsRef.current = nextItems;
    return nextItems;
  }, [cart]);
  const currentCartRef = useRef(cart);
  const currentUploadsRef = useRef(uploads || []);
  const pendingAgentItemsRef = useRef(agentItems);
  currentCartRef.current = cart;
  currentUploadsRef.current = uploads || [];
  pendingAgentItemsRef.current = agentItems;
  const priorAttempt = readAgentDraftAttempt(merchant.merchant_id, order.order_id);
  const [contact, setContact] = useState(() => {
    const shipping = order.shipping_address || order.billing_address || {};
    const initial = {
      customer_first_name: address?.firstName || order.customer_first_name || shipping.first_name || "",
      customer_last_name: address?.lastName || order.customer_last_name || shipping.last_name || "",
      customer_email: order.customer_email || email || order.user_email || "",
      customer_phone_number: order.customer_phone_number || order.user_phone_number || phone || shipping.phone || "",
      customer_address_line_1: address?.addressLine1 || shipping.address1 || "",
      customer_address_line_2: address?.addressLine2 || shipping.address2 || "",
      customer_address_city: address?.city || shipping.city || "",
      customer_address_pin: address?.pincode || shipping.zip || "",
      customer_address_state: address?.state || shipping.province || "",
      customer_address_country: address?.country || shipping.country || "",
    };
    if (JSON.stringify(priorAttempt?.payload?.line_items) === JSON.stringify(agentItems)) {
      fields.forEach(([key]) => { initial[key] = priorAttempt.payload[key] || ""; });
    }
    return initial;
  });
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedDraft, setSavedDraft] = useState(() => {
    const receipt = readAgentDraftReceipt(merchant.merchant_id, order.order_id, cart) ||
      (remainingItems.length ? readMixedAgentDraftReceipt(merchant.merchant_id, order.order_id) : null);
    // A receipt saved by the earlier two-step UI should resume in the common review too.
    if (renderMixedCheckout && receipt && remainingItems.length && !receipt.mixed && !receipt.hasAgentRemaining) {
      return { ...receipt, mixed: true, agent_items: priorAttempt?.payload?.line_items || [], operational_unique_ids: [] };
    }
    return receipt;
  });
  const savedDraftRef = useRef(null);
  const confirmedDraft = savedDraft || savedDraftRef.current;
  const [continueOperational, setContinueOperational] = useState(false);
  const savingRef = useRef(false);
  const encipherencode = getEncipherencode(params);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const finishNavigation = async (action) => {
    if (savingRef.current) return;
    if (!confirmedDraft || !confirmedDraft.persisted || confirmedDraft.mixed) { action(); return; }
    savingRef.current = true;
    setSaving(true);
    setError("");
    try {
      const writes = [];
      dispatch({ type: FLUSH, result: (write) => writes.push(write) });
      await Promise.all(writes);
      if (!mountedRef.current) return;
      // FLUSH can swallow browser storage errors; prove cart cleanup is durable before retiring its key.
      if (!hasPersistedAgentCart(products, uploads)) throw new Error("Cart persistence failed");
      clearAgentDraftReceipt(merchant.merchant_id, order.order_id);
      clearAgentDraftAttempt(merchant.merchant_id, order.order_id);
      action();
    } catch (_) {
      if (mountedRef.current) setError("Your draft is saved. Please retry to continue with your selections.");
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };
  const goToProducts = () => finishNavigation(() => navigate(`${versionedPath("/products")}${encipherencode ? `?${new URLSearchParams({ encipherencode })}` : ""}`));
  const continueWithOtherSelections = () => finishNavigation(() => setContinueOperational(true));

  const submit = async (contactDetails = contact, consent = consentAccepted, operationalUniqueId = null, normalItems = null, mixed = false) => {
    if (savingRef.current) return null;
    if (mixed && !operationalUniqueId) {
      setError("Please refresh the request review and try again.");
      return null;
    }
    if (rmsConfig.request_consent && !consent) {
      setError("Please accept the request consent.");
      return null;
    }
    if (!isValidEmail(contactDetails.customer_email) && !isValidPhoneNumber(contactDetails.customer_phone_number)) {
      setError("Please provide a valid email or phone number so the store can contact you.");
      return null;
    }
    savingRef.current = true;
    setSaving(true);
    setError("");
    const cartAtStart = currentCartRef.current;
    const normalAtStart = cartAtStart.filter((item) => !isAgentDraftItem(item));
    try {
      const existingMixedReceipt = confirmedDraft?.mixed ? confirmedDraft : null;
      if (mixed && !cartAtStart.some(isAgentDraftItem) && existingMixedReceipt) {
        const nextItems = normalItems || normalAtStart;
        const nextReceipt = saveAgentDraftReceipt(merchant.merchant_id, order.order_id, {
          ...existingMixedReceipt,
          operational_unique_ids: Array.from(new Set([...(existingMixedReceipt.operational_unique_ids || []), String(operationalUniqueId)])),
        }, nextItems);
        savedDraftRef.current = nextReceipt;
        setSavedDraft(nextReceipt);
        if (!nextReceipt.persisted) {
          setError("Your agent draft is saved, but this browser could not retain its confirmation. Retry safely before submitting the other items.");
          return null;
        }
        currentCartRef.current = nextItems;
        pendingAgentItemsRef.current = [];
        mergeReturnCartContext({ selectedOrder: order, returnedProducts: nextItems });
        dispatch(addReturnedProducts(nextItems));
        const writes = [];
        dispatch({ type: FLUSH, result: (write) => writes.push(write) });
        await Promise.all(writes);
        if (!mountedRef.current) return null;
        if (!hasPersistedAgentCart(nextItems, currentUploadsRef.current)) {
          setError("Your agent draft is saved. Retry safely before submitting the other items.");
          return null;
        }
        return nextReceipt;
      }
      const payload = buildAgentDraftPayload(order, cartAtStart.filter(isAgentDraftItem), contactDetails, currentUploadsRef.current);
      const attempt = prepareAgentDraftAttempt(merchant.merchant_id, payload);
      let submittedPayload;
      let response;
      const candidates = [attempt.payload, ...attempt.payloads.slice(0, -1).reverse()];
      for (let index = 0; index < candidates.length; index += 1) {
        submittedPayload = candidates[index];
        try {
          response = await createAgentDraft({ ...submittedPayload, idempotency_key: attempt.idempotencyKey });
          break;
        } catch (err) {
          // A prior body may have committed despite a lost response. Recover its receipt under the same key.
          if (err?.response?.status !== 409 || err?.response?.data?.detail !== "This draft submission key was already used for different details." || index === candidates.length - 1) throw err;
        }
      }
      const draft = validateAgentDraftResponse(response.data);
      // Navigation may have changed the cart. Leave the original attempt for safe recovery on re-entry.
      if (!mountedRef.current) return null;
      const submittedItems = submittedPayload.line_items;
      const submittedFingerprints = new Set(submittedItems.map((item) => JSON.stringify(item)));
      const claimedMediaIds = new Set((submittedPayload.uploaded_media_ids || []).map(String));
      const latestCart = currentCartRef.current;
      const latestNormalItems = latestCart.filter((item) => !isAgentDraftItem(item));
      const normalChanged = JSON.stringify(latestNormalItems) !== JSON.stringify(normalAtStart) &&
        JSON.stringify(latestNormalItems) !== JSON.stringify(normalItems);
      const normalToKeep = mixed && normalItems && !normalChanged ? normalItems : latestNormalItems;
      const workingCart = mixed ? [...latestCart.filter(isAgentDraftItem), ...normalToKeep] : latestCart;
      const nextItems = workingCart.filter((item) => !submittedFingerprints.has(JSON.stringify(item))).map((item) => {
        if (!(item.uploaded_media_ids || []).some((id) => claimedMediaIds.has(String(id)))) return item;
        const retainedMedia = (item.uploaded_media || []).filter((media) => !claimedMediaIds.has(String(media.media)));
        return { ...item, uploaded_media_ids: item.uploaded_media_ids.filter((id) => !claimedMediaIds.has(String(id))),
          uploaded_media: retainedMedia, media_previews: retainedMedia.map((media) => media.url).filter(Boolean) };
      });
      // Keep the acknowledged receipt across reloads before the cart changes.
      const receipt = saveAgentDraftReceipt(merchant.merchant_id, order.order_id, {
        ...draft, recovered: JSON.stringify(submittedPayload) !== JSON.stringify(attempt.payload),
        ...(mixed ? { mixed: true, agent_items: submittedItems,
          operational_unique_ids: Array.from(new Set([...(existingMixedReceipt?.operational_unique_ids || []), String(operationalUniqueId)])) } : {}),
      }, nextItems);
      if (!receipt.persisted) {
        savedDraftRef.current = receipt;
        setSavedDraft(receipt);
        setError("Your draft is saved, but this browser could not retain its confirmation. Retry confirmation safely; your selections are retained.");
        return null;
      }
      const nextUploads = currentUploadsRef.current.map((item) => ({ ...item,
        uploads: (item.uploads || []).filter((media) => !claimedMediaIds.has(String(media.media))),
      })).filter((item) => item.uploads.length > 0);
      currentCartRef.current = nextItems;
      currentUploadsRef.current = nextUploads;
      pendingAgentItemsRef.current = nextItems.filter(isAgentDraftItem);
      // Only unchanged submitted selections leave the cart; later edits remain available.
      mergeReturnCartContext({ selectedOrder: order, returnedProducts: nextItems });
      // External-store subscribers may render synchronously when the cart is cleared.
      savedDraftRef.current = receipt;
      setSavedDraft(savedDraftRef.current);
      dispatch(addReturnedProducts(nextItems));
      dispatch(addUploadMediaIds(nextUploads));
      submittedItems.forEach((item) => clearReasonDraft(`${order.order_id}:${item.line_item_id}`));
      // A mixed draft is part of this final submission; prove cleanup before allowing normal payment/create.
      if (mixed) {
        const writes = [];
        dispatch({ type: FLUSH, result: (write) => writes.push(write) });
        await Promise.all(writes);
        if (!mountedRef.current) return null;
        if (!hasPersistedAgentCart(nextItems, nextUploads)) {
          setError("Your agent draft is saved. Retry safely before submitting the other items.");
          return null;
        }
        if (nextItems.some(isAgentDraftItem) || normalChanged) {
          setError("Your agent draft is saved. Review your changed selections before submitting the other items.");
          return null;
        }
      }
      // Keep a mixed draft/key across payment cancellation or failure; success retires the correlated receipt.
      return receipt;
    } catch (err) {
      if (!mountedRef.current) return null;
      setError(getApiErrorMessage(err, "Your draft could not be saved. Please retry; your selections are retained."));
      return null;
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const submitRef = useRef(submit);
  submitRef.current = submit;
  const saveMixedDraft = useCallback((details, consent, uniqueId, items) => submitRef.current(details, consent, uniqueId, items, true), []);
  const mergeProducts = useCallback((normalProducts) => [...pendingAgentItemsRef.current, ...normalProducts], []);
  const goReviewSelections = useCallback(() => {
    if (!savingRef.current) navigate(`${versionedPath("/products")}${encipherencode ? `?${new URLSearchParams({ encipherencode })}` : ""}`);
  }, [encipherencode, navigate]);
  const displayAgentItems = agentItems.length ? agentItems : confirmedDraft?.agent_items || [];
  const mixedController = useMemo(() => ({
    items: remainingItems, agentItems: displayAgentItems, pendingAgentItems: agentItems,
    receipt: confirmedDraft, error, saving, save: saveMixedDraft, mergeProducts, goReviewSelections,
  }), [remainingItems, displayAgentItems, agentItems, confirmedDraft, error, saving, saveMixedDraft, mergeProducts, goReviewSelections]);
  if (renderMixedCheckout && remainingItems.length > 0 && (agentItems.length > 0 || confirmedDraft?.mixed)) {
    return renderMixedCheckout(mixedController);
  }
  if (continueOperational || (!agentItems.length && !confirmedDraft)) return children;
  return (
    <Stack spacing={3} sx={{ maxWidth: 760, mx: "auto", px: 2, pb: 5 }}>
      <PageHeader logoSrc={merchant.brand_logo} logoAlt={merchant.shop_display_name} primaryColor={merchant.primary_theme_color} onBack={goToProducts} />
      {confirmedDraft ? <>
        <PageIntro title="Your draft has been saved" description="The store's agents can review your details and contact you to help resolve your query. No payment has been collected for this draft." />
        <Alert severity="success">Draft reference: {confirmedDraft.reference_id}</Alert>
        {confirmedDraft.recovered && <Alert severity="info">Your earlier submission was confirmed using its original details. Changes made afterward were not included. Any changed selections remain in your cart; evidence saved to this draft must be uploaded again for another request.</Alert>}
        {confirmedDraft.hasRemaining && <p>Your other selections are still in your cart. Review and submit them separately.</p>}
        {error && <Alert severity="warning">{error}</Alert>}
        <Button variant="contained" disabled={saving} onClick={() => !confirmedDraft.persisted ? submit() : confirmedDraft.hasRemaining && !confirmedDraft.hasAgentRemaining ? continueWithOtherSelections() : goToProducts()}>
          {!confirmedDraft.persisted ? "Retry confirmation" : confirmedDraft.hasAgentRemaining ? "Review remaining selections" : confirmedDraft.hasRemaining ? "Continue with other selections" : "Back to order"}
        </Button>
      </> : <>
        <PageIntro title="Connect to Agent" description="Review your details and save a draft for the store's agents. No payment is required for this draft." />
        {remainingItems.length > 0 && <Alert severity="info">First save your agent draft. Your other selections will then continue through the usual request and payment steps.</Alert>}
        {priorAttempt && <Alert severity="info">If an earlier submission was saved, we will retrieve its original reference. Your changes will not create another draft.</Alert>}
        {agentItems.map((item) => <LineItemCard key={item.line_item_id} product={item} imageSrc={item.variant_image_url} title={item.product_title} variant={item.variant_name} quantity={item.quantity} statusLabel="Connect to Agent" clickable={false}>
          <p>{item.customer_reason_label}{item.customer_sub_reason_label ? ` — ${item.customer_sub_reason_label}` : ""}</p>
          {item.customer_reason_comment && <p>{item.customer_reason_comment}</p>}
          {(item.questionnaire_answers || []).map((answer, index) => <p key={index}>{answer.question_label || answer.question_label_snapshot || "Answer"}: {String(answer.answer ?? answer.answer_text ?? answer.answer_label ?? answer.answer_value ?? "Recorded")}{answer.additional_comment ? ` — ${answer.additional_comment}` : ""}</p>)}
          {(item.uploaded_media_ids || []).length > 0 && <p>{item.uploaded_media_ids.length} evidence file(s) attached</p>}
        </LineItemCard>)}
        <h2>Contact details</h2>
        {fields.slice(0, 4).map(([key, label]) => <TextField key={key} label={label} value={contact[key]} disabled={saving} onChange={(event) => setContact({ ...contact, [key]: event.target.value })} />)}
        <details><summary>Customer address (optional)</summary><Stack spacing={2} sx={{ mt: 2 }}>{fields.slice(4).map(([key, label]) => <TextField key={key} label={label} value={contact[key]} disabled={saving} onChange={(event) => setContact({ ...contact, [key]: event.target.value })} />)}</Stack></details>
        {rmsConfig.request_consent && <FormControlLabel control={<Checkbox checked={consentAccepted} disabled={saving} onChange={(event) => setConsentAccepted(event.target.checked)} />} label={rmsConfig.request_consent_text || "I consent to the store reviewing this request and contacting me."} />}
        {error && <Alert severity="error">{error}</Alert>}
        <Button variant="contained" disabled={saving} onClick={() => submit()}>{saving ? "Saving draft…" : "Save draft for agent"}</Button>
        <Button disabled={saving} onClick={goToProducts}>Back to selections</Button>
      </>}
    </Stack>
  );
}
